// @flow
import React, { Component } from "react";
import ButtonSecondary from "./ButtonSecondary";
import Progress from "./Progress";
import Emoji from "./Emoji";
import { SocialButtons } from "./SocialIcons";
import BouncyText from "./BouncyText";
import FadeInText from "./FadeInText";
import { Motion, spring } from "react-motion";
import imgSubscribe from "../../static/01-subscribe.png";
import imgReport from "../../static/02-millennials_mobile.png";

const SHARE_ARTICLE_URL =
  "https://www.ubs.com/global/en/wealth-management/chief-investment-office/features/millennials.html";
const SHARE_TEXT =
  "How well do you really know “millennials”? Take our quiz and find out.";

type Props = {
  text: string,
  score: number,
  restart: () => void,
  showShareStuff: boolean,
  showQRCode: boolean
};

const scoreCounterSpringConfig = {
  stiffness: 60,
  damping: 18,
  precision: 1
};

const Outro = ({ text, score, restart, showShareStuff, showQRCode }: Props) =>
  <div className="outro">
    <Progress step={1} totalSteps={1} showText={false} />
    <div className="score">
      <Motion
        defaultStyle={{ value: 0 }}
        style={{ value: spring(score, scoreCounterSpringConfig) }}
      >
        {({ value }) => {
          return (
            <div>
              <div className="title">You think</div>
              <div className="scoreNumber">
                {Math.round(value)}
                <span className="scoreRef">%</span>
              </div>
              <div className="title">like a “millennial”</div>
              <div className="scoreFeedback">
                {value === score && <ScoreFeedback score={score} />}
              </div>
            </div>
          );
        }}
      </Motion>
    </div>
    <div className="share">
      {showQRCode
        ? <FadeInText
            targetValue={showShareStuff ? 1 : 0}
            items={[<QRCode />]}
          />
        : <FadeInText
            targetValue={showShareStuff ? 1 : 0}
            items={[<ReadOn />, <SocialMedia />, <Newsletter />]}
          />}
    </div>
    <div className="controls">
      <ButtonSecondary label="Restart quiz" onClick={restart} />
      <div className="credits">
        Emoji artwork is provided by
        {" "}
        <a
          href="https://www.emojione.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          EmojiOne
        </a>
        {" "}and
        is licensed under
        {" "}
        <a
          href="https://creativecommons.org/licenses/by/4.0/legalcode"
          target="_blank"
          rel="noopener noreferrer"
        >
          CC-BY 4.0
        </a>
        <br />
        Source: cross-generational external survey conducted via Google Forms.
        Data as of May 2017. N = 436
      </div>
    </div>
    <style jsx>{`
      a {
        color: #007099;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
      .outro {
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        max-height: 100vh;
        height: 500px;
        padding: 32px 20px 20px 20px;
      }
      .title {
        font-family: FrutigerLTCom;
        font-weight: 300;
        font-size: 24px;
        line-height: 26px;
        text-align: center;
        margin: 0;
        width: 100%;
      }
      .share {
        font-family: FrutigerLTCom;
        font-weight: 300;

        font-size: 16px;
        line-height: 20px;
        text-align: center;
        width: 100%;
        margin: 16px 0 auto 0;
      }
      .socialButtons {
        display: inline-flex;
        vertical-align: middle;
      }
      .score {
        font-family: FrutigerLTCom;
        font-weight: 500;
        font-size: 72px;
        line-height: 1;
        text-align: center;
        width: 100%;
        padding: 0;
        margin: auto 0;
      }
      .scoreNumber {
        text-indent: 0.2em;
        display: block;
        margin: 8px 0;
      }
      .scoreFeedback {
        margin: 24px 0 0 0;
        height: 24px;
      }
      .scoreRef {
        font-size: 24px;
      }
      .controls {
        max-width: 320px;
        margin: 0 auto 0 auto;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
      .credits {
        color: #646464;
        font-family: FrutigerLTCom;
        font-weight: 300;
        font-size: 9px;
        line-height: 1.5;
        text-align: center;
        width: 100%;
        margin: 8px 0 0 0;
      }
      .credits a {
        color: #646464;
      }
    `}</style>
  </div>;

class OutroWithTimeout extends Component {
  timeout: number;

  state = {
    timeoutDone: false
  };

  componentDidMount() {
    this.timeout = setTimeout(() => {
      this.setState(() => ({ timeoutDone: true }));
    }, 3200);
  }
  componentWillUnmount() {
    clearTimeout(this.timeout);
  }
  render() {
    return <Outro {...this.props} showShareStuff={this.state.timeoutDone} />;
  }
}

export default OutroWithTimeout;

const ReadOn = () =>
  <span className="readon">
    <a href={SHARE_ARTICLE_URL} target="_blank" rel="noopener noreferrer">
      Read on
    </a>
    {" "}to learn how millennials <br />will shape the world.
    <style jsx>{`
      .readon {
        display: inline-block;
        text-align: center;
        font-weight: 300;
        font-size: 16px;
        line-height: 1.5;
        margin: 0 0 16px 0;
      }
      a {
        color: #007099;
        text-decoration: none;
      }
      a:hover {
        text-decoration: underline;
      }
    `}</style>
  </span>;

const QRCode = () =>
  <div className="QRContainer">
    <div className="QRC">
      <div>Subscribe to CIO newsletter</div>
      <img className="QRCode" src={imgSubscribe} />
    </div>
    <div className="QRC">
      <div>Read CIO report on Millennials</div>
      <img className="QRCode" src={imgReport} />
    </div>
    <style jsx>{`
      .QRContainer {
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin: 8px 8px 16px 8px;
      }
      .QRC {
        margin: 0 8px 0 8px;
      }
      .QRCode {
        width: 96px;
        margin-top: 8px;
      }
    `}</style>
  </div>;

const Newsletter = () =>
  <div className="shareNewsletter">
    Stay up to date with our
    {" "}
    <a
      href="http://ubs.com/cio-newsletter"
      target="_blank"
      rel="noopener noreferrer"
    >
      newsletter
    </a>
    <style jsx>{`
      .shareNewsletter {
        margin: 0 0 16px 0;
      }
    `}</style>
  </div>;

const SocialMedia = () =>
  <div className="shareSocial">
    Share this quiz on
    <SocialButtons url={SHARE_ARTICLE_URL} text={SHARE_TEXT} />
    <style jsx>{`
      .shareSocial {
        margin: 0 0 16px 0;
      }
    `}</style>
  </div>;

const ScoreFeedback = ({ score }: { score: number }) =>
  <div>
    {score > 85
      ? <BouncyText
          items={[
            "Great ",
            "job",
            "! ",
            <Emoji name="clap" />,
            <Emoji name="crown" />,
            <Emoji name="confetti_ball" />
          ]}
        />
      : score > 60
        ? <BouncyText
            items={[
              "Very ",
              "nice",
              "! ",
              <Emoji name="thumbsup" />,
              <Emoji name="mortar_board" />,
              <Emoji name="tada" />
            ]}
          />
        : <BouncyText
            items={[
              "Better luck ",
              "next time",
              "! ",
              <Emoji name="wave" />,
              <Emoji name="carrot" />,
              <Emoji name="third_place" />
            ]}
          />}
    <style jsx>{`
      div {
        text-align: center;
        font-family: FrutigerLTCom;
        font-weight: 700;
        font-size: 18px;
      }
    `}</style>
  </div>;



// WEBPACK FOOTER //
// ./src/components/Quiz/Outro.js